import { collection, onSnapshot } from "firebase/firestore";
import { httpsCallable } from "firebase/functions";
import { db, functions } from "../firebase";
import { COMMUNITY_INFORMATION_PROVIDER_MODE } from "./config";

export const COMMUNITY_EVENT_DRAFT_FIELDS = [
  "title",
  "description",
  "organiserName",
  "venueName",
  "address",
  "postcode",
  "startDate",
  "endDate",
  "startTime",
  "endTime",
  "timezone",
  "rawDateTimeText",
  "recurrenceText",
  "priceAmount",
  "priceCurrency",
  "priceText",
  "audience",
  "ageRestrictions",
  "bookingMethod",
  "bookingUrl",
  "accessibility",
  "publicContact",
];

const NULLABLE_TEXT_FIELDS = COMMUNITY_EVENT_DRAFT_FIELDS.filter(
  (field) => field !== "priceAmount"
);

function cleanText(value, maxLength) {
  return String(value ?? "").trim().slice(0, maxLength);
}

function timestampToMillis(value) {
  if (!value) return 0;
  if (typeof value.toMillis === "function") return value.toMillis();
  if (typeof value.toDate === "function") return value.toDate().getTime();
  if (typeof value === "object" && Number.isFinite(value.seconds)) {
    return value.seconds * 1000;
  }
  const parsed = new Date(value).getTime();
  return Number.isFinite(parsed) ? parsed : 0;
}

export function sanitiseCommunityEventDraft(draft = {}) {
  const result = {
    schemaVersion: cleanText(draft.schemaVersion || "1.0", 20),
  };
  NULLABLE_TEXT_FIELDS.forEach((field) => {
    const value = draft[field];
    result[field] = value === null || value === undefined || String(value).trim() === ""
      ? null
      : String(value).trim();
  });
  const amount = draft.priceAmount;
  if (amount === null || amount === undefined || String(amount).trim() === "") {
    result.priceAmount = null;
  } else {
    const numericAmount = Number(amount);
    if (!Number.isFinite(numericAmount) || numericAmount < 0 || numericAmount > 1000000) {
      throw new Error("Price amount must be a non-negative number or left blank.");
    }
    result.priceAmount = numericAmount;
  }
  result.suggestedTags = Array.isArray(draft.suggestedTags)
    ? draft.suggestedTags.map((item) => cleanText(item, 60)).filter(Boolean).slice(0, 12)
    : [];
  result.evidence = Array.isArray(draft.evidence)
    ? draft.evidence.slice(0, 80).map((item = {}) => ({
        field: cleanText(item.field, 80),
        sourceId: cleanText(item.sourceId, 120),
        excerpt: item.excerpt ? cleanText(item.excerpt, 500) : null,
        imageLocation: item.imageLocation ? cleanText(item.imageLocation, 200) : null,
      }))
    : [];
  result.missingFields = Array.isArray(draft.missingFields)
    ? draft.missingFields.map((item) => cleanText(item, 80)).filter(Boolean)
    : [];
  result.conflictingFields = Array.isArray(draft.conflictingFields)
    ? draft.conflictingFields.slice(0, 30).map((item = {}) => ({
        field: cleanText(item.field, 80),
        sourceIds: Array.isArray(item.sourceIds)
          ? item.sourceIds.map((sourceId) => cleanText(sourceId, 120)).filter(Boolean).slice(0, 6)
          : [],
        reason: cleanText(item.reason, 500),
      }))
    : [];
  result.warnings = Array.isArray(draft.warnings)
    ? draft.warnings.slice(0, 40).map((item = {}) => ({
        code: cleanText(item.code, 80),
        field: item.field ? cleanText(item.field, 80) : null,
        message: cleanText(item.message, 500),
      }))
    : [];
  result.clarificationQuestions = Array.isArray(draft.clarificationQuestions)
    ? draft.clarificationQuestions
        .map((item) => cleanText(item, 500))
        .filter(Boolean)
        .slice(0, 30)
    : [];
  result.clarificationMessage = cleanText(draft.clarificationMessage, 2400);
  return result;
}

export function subscribeCommunityInformationEvents(onData, onError) {
  return onSnapshot(
    collection(db, "events"),
    (snapshot) => {
      const events = snapshot.docs
        .map((item) => ({ id: item.id, ...item.data() }))
        .filter((event) => event.status === "pending")
        .sort(
          (first, second) =>
            timestampToMillis(second.createdAt || second.eventDate) -
              timestampToMillis(first.createdAt || first.eventDate) ||
            String(first.title || first.name || "").localeCompare(
              String(second.title || second.name || "")
            )
        );
      onData(events);
    },
    onError
  );
}

async function callCommunityInformationFunction(name, payload) {
  const callable = httpsCallable(functions, name);
  const response = await callable(payload);
  return response.data;
}

function reviewIdentity(input = {}) {
  const reviewId = cleanText(input.reviewId, 200);
  if (!reviewId) throw new Error("A review ID is required.");
  const expectedVersion = String(input.expectedVersion ?? "").trim();
  if (!/^[a-f0-9]{64}$/i.test(expectedVersion)) {
    throw new Error("A current review version is required.");
  }
  return { reviewId, expectedVersion };
}

export function prepareCommunityEventDraft(input = {}) {
  if (!["mock", "openai"].includes(COMMUNITY_INFORMATION_PROVIDER_MODE)) {
    throw new Error(
      "The Community Information provider is not enabled for this runtime.",
    );
  }
  const sourceType =
    input.sourceType === "partner_text" ? "partner_text" : "event_submission";
  const payload = {
    sourceType,
    providerMode: COMMUNITY_INFORMATION_PROVIDER_MODE,
  };

  if (sourceType === "event_submission") {
    payload.submissionId = cleanText(input.submissionId, 200);
    if (!payload.submissionId) throw new Error("Choose a pending event submission.");
  } else {
    payload.partnerText = String(input.partnerText ?? "").slice(0, 12000);
    payload.sourceUrl = cleanText(input.sourceUrl, 2000);
    payload.idempotencyKey = cleanText(input.idempotencyKey, 120);
    if (!payload.partnerText.trim()) throw new Error("Paste the partner event information.");
    if (payload.idempotencyKey.length < 12) {
      throw new Error("A stable intake key is required for pasted information.");
    }
  }
  if (input.retry === true) payload.retry = true;

  return callCommunityInformationFunction(
    "prepareCommunityEventDraft",
    payload
  );
}

export function saveCommunityEventDraft(input = {}) {
  return callCommunityInformationFunction("saveCommunityEventDraft", {
    ...reviewIdentity(input),
    draft: sanitiseCommunityEventDraft(input.draft),
  });
}

export function markCommunityEventNeedsInformation(input = {}) {
  return callCommunityInformationFunction(
    "markCommunityEventNeedsInformation",
    {
      ...reviewIdentity(input),
      clarificationMessage: cleanText(input.clarificationMessage, 4000),
    }
  );
}

export function rejectCommunityEventDraft(input = {}) {
  return callCommunityInformationFunction("rejectCommunityEventDraft", {
    ...reviewIdentity(input),
    reason: cleanText(input.reason, 500),
  });
}

export function approveCommunityEventDraft(input = {}) {
  return callCommunityInformationFunction("approveCommunityEventDraft", {
    ...reviewIdentity(input),
  });
}

export const communityInformationService = {
  subscribeCommunityInformationEvents,
  prepareCommunityEventDraft,
  saveCommunityEventDraft,
  markCommunityEventNeedsInformation,
  rejectCommunityEventDraft,
  approveCommunityEventDraft,
};
